<script setup>
import { ref } from "vue";

defineProps({
  deactivationDate: {
    type: String,
    required: true
  }
});

const emit = defineEmits(["close", "confirm"]);
const isReactivating = ref(false);

// ====================================================================
// ✅ LÓGICA DE TOAST (ESTILO PROFILEFORM) 🚀
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);

function resetToastTimeout(duration) {
  return new Promise((resolve) => {
    if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
    toastTimeoutId.value = setTimeout(() => {
      showToast.value = false;
      toastTimeoutId.value = null;
      resolve();
    }, duration);
  });
}

async function showSuccessToast(message) {
  toastMessage.value = message;
  showToast.value = true;
  // Aguarda o tempo do toast antes de permitir o redirecionamento
  await resetToastTimeout(2500);
}
// ====================================================================

async function handleConfirm() {
  if (isReactivating.value) return;
  
  isReactivating.value = true;
  // O pai (LoginForm) deve ser uma função async que chama a reativação
  // Nós emitimos o evento e oLoginForm processará.
  emit("confirm", async (success) => {
    if (success) {
      await showSuccessToast("Conta reativada com sucesso! Entrando...");
      return true; // Sinaliza que pode prosseguir
    } else {
      isReactivating.value = false;
    }
  });
}
</script>

<template>
  <div class="modal-overlay">
    <div class="modal-content-custom">
      <div class="login-body text-center space-y-4">
        <h3 class="font-bold text-xl">Reativar Conta?</h3>
        
        <p class="text-sm text-justify">
          Sua conta foi desativada em <strong>{{ deactivationDate }}</strong>. 
          Deseja reativá-la agora? Você manterá todos os seus dados e seu plano será atualizado para o <strong>Free</strong>.
        </p>

        <div class="flex flex-col gap-2 pt-2">
          <button 
            @click="handleConfirm" 
            class="btn btn-primary" 
            :disabled="isReactivating"
          >
            <span v-if="isReactivating">Processando...</span>
            <span v-else>Sim, Reativar Minha Conta</span>
          </button>
          
          <button 
            @click="emit('close')" 
            class="btn btn-secondary"
            :disabled="isReactivating"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[100000] flex items-start justify-center pt-6 px-4"
      >
        <div class="toast toast-success pointer-events-auto">
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">Sucesso!</p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  align-items: flex-start !important;
  justify-content: center !important;
}

.modal-content-custom {
  width: 100%;
  max-width: 24rem;
  background-color: var(--surface) !important;
  border-radius: 16px !important;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1) !important;
  overflow: hidden;
  border: 1px solid var(--neutral-200);
}

.login-body { padding: 1.5rem; }
.text-center { text-align: center; }
.text-justify { text-align: justify; }

.btn {
  width: 100%;
}

.toast-title {
  margin-bottom: 0.2rem;
}

.fixed { position: fixed; }
.inset-0 { inset: 0; }
.z-\[100000\] { z-index: 100000; }
.items-start { align-items: flex-start; }
.justify-center { justify-content: center; }
.pt-6 { padding-top: 1.5rem; }
.px-4 { padding-left: 1rem; padding-right: 1rem; }
</style>